import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './styles/SortFilterDropdown.module.css';
import TagSearch from '../scenarios/create/TagSearch';

const CREATOR_DEBOUNCE_MS = 400;

const ACCESS_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'public', label: 'Public' },
    { value: 'protected', label: 'Password Protected' },
];

/**
 * Forum-style sort & filter popover shared by scenario listings.
 *
 * Fully controlled: the parent owns a single controls object and this component
 * reports edits as partial updates. Values are stable enum keys (never display
 * labels) so they can be sent straight to the API.
 *
 * @param {{ value: string, label: string }[]} sortOptions - Sort fields to offer.
 * @param {{ sortBy: string, sortOrder: 'asc'|'desc', filterBy: string, creator: string, tags: object[] }} value
 * @param {(partial: object) => void} onChange - Called with the changed subset of `value`.
 * @param {boolean} [showAccessFilter=false] - Show the Public/Password-Protected filter.
 * @param {boolean} [showCreatorSearch=false] - Show the creator username search.
 * @param {boolean} [showTagSearch=false] - Embed TagSearch for multi-tag filtering.
 */
const SortFilterDropdown = ({
    sortOptions,
    value,
    onChange,
    showAccessFilter = false,
    showCreatorSearch = false,
    showTagSearch = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [creatorInput, setCreatorInput] = useState(value.creator || '');
    const wrapperRef = useRef(null);
    const creatorDebounceRef = useRef(null);

    // Keep the local creator input in sync if the parent resets it externally.
    useEffect(() => {
        setCreatorInput(value.creator || '');
    }, [value.creator]);

    useEffect(() => {
        if (!isOpen) return;
        const handleOutsideClick = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    useEffect(() => () => clearTimeout(creatorDebounceRef.current), []);

    const handleSortSelect = (sortValue) => {
        onChange({ sortBy: sortValue });
    };

    const toggleSortOrder = () => {
        onChange({ sortOrder: value.sortOrder === 'asc' ? 'desc' : 'asc' });
    };

    const commitCreator = (text) => {
        const trimmed = text.trim();
        if (trimmed !== (value.creator || '')) {
            onChange({ creator: trimmed });
        }
    };

    const handleCreatorInput = (event) => {
        const text = event.target.value;
        setCreatorInput(text);
        clearTimeout(creatorDebounceRef.current);
        creatorDebounceRef.current = setTimeout(() => commitCreator(text), CREATOR_DEBOUNCE_MS);
    };

    const handleCreatorKeyDown = (event) => {
        if (event.key === 'Enter') {
            clearTimeout(creatorDebounceRef.current);
            commitCreator(creatorInput);
        }
    };

    const handleSelectTag = useCallback((selectedItem) => {
        const tagName = typeof selectedItem === 'string' ? selectedItem : selectedItem.name;
        if (value.tags.some(t => t.name.toLowerCase() === tagName.toLowerCase())) {
            return;
        }
        const tagId = typeof selectedItem === 'string' ? tagName : (selectedItem._id ?? tagName);
        onChange({ tags: [...value.tags, { _id: tagId, name: tagName }] });
    }, [value.tags, onChange]);

    const handleRemoveTag = useCallback((tagToRemove) => {
        onChange({ tags: value.tags.filter(t => t.name !== tagToRemove.name) });
    }, [value.tags, onChange]);

    const activeCount =
        (value.sortBy ? 1 : 0) +
        (value.filterBy ? 1 : 0) +
        (value.creator ? 1 : 0) +
        value.tags.length;

    const activeSortLabel = sortOptions.find(opt => opt.value === value.sortBy)?.label;
    const accessLabel = ACCESS_OPTIONS.find(opt => opt.value === value.filterBy)?.label;

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <button
                type="button"
                className={`${styles.triggerButton} ${isOpen ? styles.triggerButtonOpen : ''}`}
                onClick={() => setIsOpen(prev => !prev)}
                aria-expanded={isOpen}
                aria-haspopup="true"
            >
                <svg className={styles.triggerIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
                </svg>
                Sort &amp; Filter
                {activeCount > 0 && <span className={styles.badge}>{activeCount}</span>}
            </button>

            {activeCount > 0 && (
                <div className={styles.activeChips}>
                    {value.sortBy && (
                        <span className={styles.chip}>
                            {activeSortLabel} {value.sortOrder === 'asc' ? '↑' : '↓'}
                            <button type="button" className={styles.chipRemove} onClick={() => onChange({ sortBy: '' })} aria-label="Clear sort">×</button>
                        </span>
                    )}
                    {value.filterBy && (
                        <span className={styles.chip}>
                            {accessLabel}
                            <button type="button" className={styles.chipRemove} onClick={() => onChange({ filterBy: '' })} aria-label="Clear access filter">×</button>
                        </span>
                    )}
                    {value.creator && (
                        <span className={styles.chip}>
                            by {value.creator}
                            <button type="button" className={styles.chipRemove} onClick={() => onChange({ creator: '' })} aria-label="Clear creator filter">×</button>
                        </span>
                    )}
                    {value.tags.map(tag => (
                        <span key={tag._id} className={styles.chip}>
                            #{tag.name}
                            <button type="button" className={styles.chipRemove} onClick={() => handleRemoveTag(tag)} aria-label={`Remove tag ${tag.name}`}>×</button>
                        </span>
                    ))}
                </div>
            )}

            {isOpen && (
                <div className={styles.panel} role="menu">
                    <div className={styles.section}>
                        <div className={styles.sectionHeader}>
                            <span className={styles.sectionTitle}>Sort by</span>
                            <button
                                type="button"
                                className={styles.orderToggle}
                                onClick={toggleSortOrder}
                                disabled={!value.sortBy}
                                title={value.sortOrder === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
                                aria-label={`Sort direction: ${value.sortOrder === 'asc' ? 'ascending' : 'descending'}`}
                            >
                                {value.sortOrder === 'asc' ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                        <path d="M12 4l-7 7h4v9h6v-9h4l-7-7z"/>
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                        <path d="M12 20l7-7h-4V4H9v9H5l7 7z"/>
                                    </svg>
                                )}
                            </button>
                        </div>
                        <div className={styles.optionList}>
                            <button
                                type="button"
                                className={`${styles.option} ${!value.sortBy ? styles.optionSelected : ''}`}
                                onClick={() => handleSortSelect('')}
                            >
                                Default
                            </button>
                            {sortOptions.map(opt => (
                                <button
                                    type="button"
                                    key={opt.value}
                                    className={`${styles.option} ${value.sortBy === opt.value ? styles.optionSelected : ''}`}
                                    onClick={() => handleSortSelect(opt.value)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {showAccessFilter && (
                        <div className={styles.section}>
                            <span className={styles.sectionTitle}>Access</span>
                            <div className={styles.optionList}>
                                {ACCESS_OPTIONS.map(opt => (
                                    <button
                                        type="button"
                                        key={opt.value || 'all'}
                                        className={`${styles.option} ${value.filterBy === opt.value ? styles.optionSelected : ''}`}
                                        onClick={() => onChange({ filterBy: opt.value })}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {showCreatorSearch && (
                        <div className={styles.section}>
                            <label className={styles.sectionTitle} htmlFor="sortFilterCreatorInput">Creator</label>
                            <input
                                id="sortFilterCreatorInput"
                                type="search"
                                className={styles.creatorInput}
                                placeholder="Search by username..."
                                value={creatorInput}
                                onChange={handleCreatorInput}
                                onKeyDown={handleCreatorKeyDown}
                            />
                        </div>
                    )}

                    {showTagSearch && (
                        <div className={styles.section}>
                            <span className={styles.sectionTitle}>Tags</span>
                            <TagSearch
                                selectedTags={value.tags}
                                onSelectTag={handleSelectTag}
                                onRemoveTag={handleRemoveTag}
                                placeholder="Search tags..."
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SortFilterDropdown;
